import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { fetchDataFixesSummary } from "@/lib/api";
import api from "@/lib/api";
import { localTime } from "@/lib/time";
import {
  Wrench,
  Medal,
  LayoutDashboard,
  Package,
  Truck,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  FileSpreadsheet,
  Zap,
  Satellite,
  Users,
  Award,
  Crown,
  Map,
  GitCompare,
  Grid3X3,
  Fuel,
  CreditCard,
  UserCog,
  RadioTower,
  Landmark,
  Plug,
  ChevronDown,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { SIDEBAR } from "@/constants/testIds";
import { cn } from "@/lib/utils";

export const navItems = [
  { key: "overview", to: "/", icon: LayoutDashboard, label: "Overview", testId: SIDEBAR.overview },
  { key: "shipments", to: "/shipments", icon: Package, label: "Shipments", testId: SIDEBAR.shipments },
  { key: "fleet", to: "/fleet", icon: Truck, label: "Fleet", testId: SIDEBAR.fleet },
  { key: "analytics", to: "/analytics", icon: BarChart3, label: "Analytics", testId: SIDEBAR.analytics },
  { key: "comparison", to: "/comparison", icon: FileSpreadsheet, label: "Comparison", testId: "sidebar-comparison" },
  { key: "trucks-on-road", to: "/trucks-on-road", icon: Users, label: "Trucks & Drivers", testId: "sidebar-trucks-on-road" },
  { key: "driver-scorecard", to: "/driver-scorecard", icon: Award, label: "Driver Scorecard", testId: "sidebar-driver-scorecard" },
  { key: "dispatcher-leaderboard", to: "/dispatcher-leaderboard", icon: Crown, label: "Dispatchers", testId: "sidebar-dispatcher-leaderboard" },
  { key: "dispatcher-rankings", to: "/dispatcher-rankings", icon: Medal, label: "Dispatcher Rankings", testId: "sidebar-dispatcher-rankings" },
  { key: "data-fixes", to: "/data-fixes", icon: Wrench, label: "Data Fixes", testId: "sidebar-data-fixes", badge: "dataFixes" },
  { key: "dispatcher-comparison", to: "/dispatcher-comparison", icon: GitCompare, label: "Compare Dispatchers", testId: "sidebar-dispatcher-comparison" },
  { key: "load-heatmap", to: "/load-heatmap", icon: Grid3X3, label: "Load Heatmap", testId: "sidebar-load-heatmap" },
  { key: "fuel-report", to: "/fuel-report", icon: Fuel, label: "Fuel Report", testId: "sidebar-fuel-report" },
  { key: "relay-fuel", to: "/relay-fuel", icon: CreditCard, label: "Relay Fuel", testId: "sidebar-relay-fuel", group: "integrations" },
  { key: "fleet-map", to: "/fleet-map", icon: Map, label: "Fleet Map", testId: "sidebar-fleet-map" },
  { key: "alvys", to: "/alvys", icon: Zap, label: "Alvys TMS", testId: "sidebar-alvys", group: "integrations" },
  { key: "orient", to: "/orient", icon: RadioTower, label: "Orient ELD", testId: "sidebar-orient", group: "integrations" },
  { key: "ifta", to: "/ifta", icon: Landmark, label: "IFTA & Tax", testId: "sidebar-ifta" },
  { key: "samsara", to: "/samsara", icon: Satellite, label: "Samsara ELD", testId: "sidebar-samsara", group: "integrations" },
];

// Integration health for the side-menu dots; refreshes every 5 minutes
const HEALTH_KEY = { "relay-fuel": "relay", alvys: "alvys", orient: "orient", samsara: "samsara" };
const DOT = { ok: "bg-emerald-400", warn: "bg-amber-400", down: "bg-red-500 animate-pulse" };
const useHealth = () => {
  const [h, setH] = useState({});
  useEffect(() => {
    const load = () => { if (!document.hidden) api.get("/integrations/health").then((r) => setH(r.data)).catch(() => {}); };
    load();
    const id = setInterval(load, 300000);
    return () => clearInterval(id);
  }, []);
  return h;
};
const Dot = ({ s, testId, collapsed }) => (s ? (
  <span data-testid={testId} data-status={s.status} title={`${s.detail} · checked ${localTime(s.checked_at)}`}
    className={cn("h-2 w-2 shrink-0 rounded-full", DOT[s.status], collapsed ? "absolute ml-4 -mt-4" : "ml-auto")} />
) : null);

// Open data-fix count for last week; refreshes every minute and when the Data Fixes page changes something
const useOpenFixes = (enabled) => {
  const [open, setOpen] = useState(0);
  useEffect(() => {
    if (!enabled) return undefined;
    const load = () => { if (!document.hidden) fetchDataFixesSummary().then(r => r.open != null && setOpen(r.open)).catch(() => {}); };
    load();
    const id = setInterval(load, 300000);
    window.addEventListener("data-fixes-changed", load);
    return () => { clearInterval(id); window.removeEventListener("data-fixes-changed", load); };
  }, [enabled]);
  return open;
};

export default function Sidebar({ collapsed, onToggle }) {
  const { canAccess } = useAuth();
  const { pathname } = useLocation();
  const fixOpen = useOpenFixes(canAccess("data-fixes"));
  const health = useHealth();
  const items = navItems.filter((n) => canAccess(n.key));
  const main = items.filter((n) => !n.group), integ = items.filter((n) => n.group === "integrations");
  const worst = ["down", "warn", "ok"].find((st) => integ.some((n) => health[HEALTH_KEY[n.key]]?.status === st));
  const downNames = integ.filter((n) => health[HEALTH_KEY[n.key]]?.status !== "ok" && health[HEALTH_KEY[n.key]]).map((n) => n.label);
  const integActive = integ.some((n) => pathname.startsWith(n.to));
  const [integOpen, setIntegOpen] = useState(() => localStorage.getItem("nav-integrations-open") === "1");
  const toggleInteg = () => setIntegOpen((v) => { localStorage.setItem("nav-integrations-open", v ? "0" : "1"); return !v; });
  useEffect(() => { if (integActive) setIntegOpen(true); }, [integActive]);
  const users = canAccess("admin") ? [{ key: "users", to: "/users", icon: UserCog, label: "Users & Access", testId: "sidebar-users" }] : [];
  const link = ({ key, to, icon: Icon, label, testId, badge }, sub) => (
    <NavLink
      key={to}
      to={to}
      end={to === "/"}
      data-testid={testId}
      className={({ isActive: active }) => { const isActive = active || (to === "/dispatcher-leaderboard" && pathname.startsWith("/dispatcher/")); return (
        cn(
          "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200",
          sub && !collapsed && "ml-4 py-2",
          isActive
            ? "bg-blue-600/15 text-blue-400 border border-blue-500/20"
            : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/50 border border-transparent"
        )); }
      }
    >
      <Icon className="h-[18px] w-[18px] shrink-0" />
      {!collapsed && <span className="whitespace-nowrap">{label}</span>}
      {badge && fixOpen > 0 && (
        <span data-testid="sidebar-data-fixes-badge" title="Open data fixes for last week" className={cn("rounded-full bg-amber-500/20 px-1.5 text-[10px] font-bold text-amber-300", collapsed ? "absolute ml-4 -mt-4" : "ml-auto")}>{fixOpen}</span>
      )}
      {sub && <Dot s={health[HEALTH_KEY[key]]} testId={`sidebar-health-${HEALTH_KEY[key]}`} collapsed={collapsed} />}
    </NavLink>
  );
  return (
    <aside
      data-testid={SIDEBAR.nav}
      className={cn(
        "fixed left-0 top-0 z-40 h-screen border-r border-slate-800/60 bg-[#0B1120] transition-all duration-300 flex flex-col",
        collapsed ? "w-[68px]" : "w-64"
      )}
    >
      {/* Logo */}
      <div className="flex h-16 items-center gap-3 border-b border-slate-800/60 px-4">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-600 font-bold text-white text-sm font-['Outfit']">
          AFT
        </div>
        {!collapsed && (
          <span className="text-base font-bold text-slate-100 tracking-tight font-['Outfit'] whitespace-nowrap">
            Freight Command
          </span>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
        {main.map((n) => link(n))}
        {integ.length > 0 && (
          <>
            <button type="button" data-testid="sidebar-integrations" onClick={toggleInteg} title="Integrations"
              className={cn("flex w-full items-center gap-3 rounded-lg border border-transparent px-3 py-2.5 text-sm font-medium transition-colors duration-200 hover:bg-slate-800/50 hover:text-slate-200",
                integActive && !integOpen ? "text-blue-400" : "text-slate-400")}>
              <Plug className="h-[18px] w-[18px] shrink-0" />
              {!collapsed && <span className="whitespace-nowrap">Integrations</span>}
              {worst && <span data-testid="sidebar-health-summary" data-status={worst} title={downNames.length ? `Not syncing: ${downNames.join(", ")}` : "All integrations syncing"}
                className={cn("h-2 w-2 shrink-0 rounded-full", DOT[worst], collapsed ? "absolute ml-4 -mt-4" : "ml-auto")} />}
              {!collapsed && <ChevronDown className={cn("h-4 w-4 transition-transform duration-200", worst ? "" : "ml-auto", integOpen ? "" : "-rotate-90")} />}
            </button>
            {integOpen && <div data-testid="sidebar-integrations-menu" className="space-y-1">{integ.map((n) => link(n, true))}</div>}
          </>
        )}
        {users.map((n) => link(n))}
      </nav>

      {/* Collapse toggle */}
      <div className="border-t border-slate-800/60 p-3">
        <button
          data-testid={SIDEBAR.collapse}
          onClick={onToggle}
          className="flex w-full items-center justify-center rounded-lg p-2 text-slate-400 hover:bg-slate-800/50 hover:text-slate-200 transition-colors"
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </button>
      </div>
    </aside>
  );
}
